import { ref, computed } from 'vue';
import store from '@/store';
import { config } from '@/config';
import { t } from '@/utils/i18n-helper';

/**
 * Composable para gestionar la lógica de la venta privada SGA (Amigo/Abonado)
 * Sigue el Playbook v1.0: Lógica de negocio fuera de los componentes .vue
 */
export function useAmigoAbonadoSga() {
    // ── ESTADO REACTIVO ──────────────────────────────────────────────────────
    const loading = ref(false);
    const profiles = ref([]);
    const selectedProfile = ref(null);
    const entitlements = ref([]);
    const selectedEntitlementId = ref(null);
    const error = ref(null);

    // ── PROPIEDADES COMPUTADAS ───────────────────────────────────────────────
    const hasProfiles = computed(() => profiles.value.length > 0);
    const hasEntitlements = computed(() => entitlements.value.length > 0);
    const buyerId = computed(() => selectedProfile.value?.sga_profile_id || null);
    
    /**
     * Auth guard: refleja si el usuario actual tiene una sesión válida.
     * Lee desde store.getters.isLogged (AuthModule).
     * Implementa el requisito del spec: "El sistema DEBE verificar que el usuario tiene sesión activa"
     */
    const isAuthenticated = computed(() => {
        return store.getters.isLogged;
    });

    // ── MÉTODOS ──────────────────────────────────────────────────────────────
    
    /**
     * Obtiene los perfiles SGA del usuario logueado
     * 
     * GUARD: Si el usuario NO tiene una sesión válida (no autenticado),
     * establece error.value = 'auth_required' y retorna temprano.
     * Esto previene llamadas API no autorizadas (requisito del spec).
     */
    async function fetchProfiles() {
        // ── AUTH GUARD ───────────────────────────────────────────────────────
        if (!isAuthenticated.value) {
            error.value = 'auth_required';
            loading.value = false;
            return;
        }
        
        loading.value = true;
        error.value = null;
        try {
            const res = await store.dispatch(config.getVuexAlias('front.perfil.get.sga'), {});
            profiles.value = Array.isArray(res.data) ? res.data : [res.data];
            return profiles.value;
        } catch (err) {
            error.value = t('front.sga.checkout.error_perfiles');
            console.error(err);
            throw err;
        } finally {
            loading.value = false;
        }
    }

    /**
     * Obtiene los derechos de compra (entitlements) para el perfil seleccionado
     */
    async function fetchEntitlements() {
        if (!buyerId.value) return;
        
        loading.value = true;
        error.value = null;
        try {
            const res = await store.dispatch(config.getVuexAlias('sga.cart.entitlements'), {
                filters: { buyer_id: buyerId.value }
            });
            entitlements.value = res.data || [];
            return entitlements.value;
        } catch (err) {
            error.value = t('front.sga.checkout.error_derechos');
            console.error(err);
            throw err;
        } finally {
            loading.value = false;
        }
    }

    /**
     * Selecciona un perfil y dispara la carga de abonos
     */
    async function selectProfile(profile) {
        selectedProfile.value = profile;
        selectedEntitlementId.value = null;
        return await fetchEntitlements();
    }

    /**
     * Selecciona un entitlement
     */
    function selectEntitlement(id) {
        selectedEntitlementId.value = id;
    }

    return {
        // Estado
        loading,
        profiles,
        selectedProfile,
        selectedProfileId: computed(() => selectedProfile.value?.sga_profile_id || null),
        entitlements,
        selectedEntitlementId,
        error,
        // Computed
        hasProfiles,
        hasEntitlements,
        buyerId,
        isAuthenticated,
        // Métodos
        fetchProfiles,
        fetchEntitlements,
        selectProfile,
        selectEntitlement
    };
}
